import React, { useEffect, useState } from 'react';
import { ArrowLeft, Calendar, Clock, Mail, User, Briefcase, FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/consultations`);
      setBookings(response.data);
    } catch (err) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4 text-orange-500" size={48} />
          <p className="text-gray-600">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Consultation Bookings</h1>
              <p className="text-gray-600 mt-1">Manage your consultation requests</p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={fetchBookings}
                className="border-orange-500 text-orange-500 hover:bg-orange-50"
              >
                <RefreshCw size={16} className="mr-2" />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-orange-500 text-orange-500 hover:bg-orange-50"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Total Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-500">{bookings.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">This Week</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-500">
                {bookings.filter(b => {
                  const bookingDate = new Date(b.created_at);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return bookingDate >= weekAgo;
                }).length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Today</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-orange-500">
                {bookings.filter(b => {
                  const bookingDate = new Date(b.created_at);
                  const today = new Date();
                  return bookingDate.toDateString() === today.toDateString();
                }).length}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bookings List */}
        <div className="space-y-4">
          {bookings.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Calendar className="mx-auto mb-4 text-gray-400" size={48} />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No bookings yet</h3>
                <p className="text-gray-600">Consultation bookings will appear here</p>
              </CardContent>
            </Card>
          ) : (
            bookings.map((booking) => (
              <Card key={booking.id} className="border-2 hover:border-orange-500 transition-all duration-300">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl">{booking.name}</CardTitle>
                      <CardDescription className="mt-1">
                        Booked on {formatDateTime(booking.created_at)}
                      </CardDescription>
                    </div>
                    <Badge className="bg-orange-500 text-white">
                      Pending
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Mail className="text-orange-500" size={20} />
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="font-semibold">{booking.email}</p>
                        </div>
                      </div>
                      
                      {booking.business_type && (
                        <div className="flex items-center space-x-3">
                          <Briefcase className="text-orange-500" size={20} />
                          <div>
                            <p className="text-sm text-gray-600">Business Type</p>
                            <p className="font-semibold">{booking.business_type}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <Calendar className="text-orange-500" size={20} />
                        <div>
                          <p className="text-sm text-gray-600">Preferred Date</p>
                          <p className="font-semibold">{formatDate(booking.preferred_date)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Clock className="text-orange-500" size={20} />
                        <div>
                          <p className="text-sm text-gray-600">Preferred Time</p>
                          <p className="font-semibold">{booking.preferred_time} IST</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {booking.notes && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-start space-x-3">
                        <FileText className="text-orange-500 mt-1" size={20} />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600 mb-1">Additional Notes</p>
                          <p className="text-gray-800">{booking.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t">
                    <Button
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={() => window.open(`mailto:${booking.email}`, '_blank')}
                    >
                      Send Meet Link via Email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
